import { HowToAnswer } from "../components/interactions/HowToAnswer";
/**
 * WCC360 v2 — Skills Intelligence™ evidence screens.
 *
 * Two to four screens, depending on band, each asking two or three items from
 * the frozen 40-item evidence bank. Every item is "have you done / used /
 * shipped this" — never a self-rating, never a Likert row.
 *
 * "Nothing here yet" is a real answer: Continue is always available and an
 * empty screen is recorded as empty, not as a midpoint. Nothing on these
 * screens can remove a career from anyone's results.
 */
import { Check, ArrowRight } from "lucide-react";
import { useAssessmentV2 } from "../store";
import { useScreenDraft } from "../useScreenDraft";
import {
  SKILLS_SCREEN_COPY, itemsForScreen, type SkillsScreenAnswer,
} from "../skillsEvidence";
import { cn } from "@/lib/utils";

interface Props { screenId: string; onNext: () => void }

export function SkillsEvidenceScreen({ screenId, onNext }: Props) {
  const { state, setPart2, band } = useAssessmentV2();
  const items = itemsForScreen(screenId);
  const copy = SKILLS_SCREEN_COPY[screenId];

  const saved = state.part2?.[screenId] as SkillsScreenAnswer | undefined;
  const [draft, setDraft, clearDraft] = useScreenDraft<SkillsScreenAnswer>(
    band, screenId, {}, saved ?? null,
  );

  const toggle = (itemId: string, optionId: string, multi: boolean) => {
    setDraft((prev) => {
      const current = prev[itemId] ?? [];
      if (!multi) {
        return { ...prev, [itemId]: current[0] === optionId ? [] : [optionId] };
      }
      return {
        ...prev,
        [itemId]: current.includes(optionId)
          ? current.filter((x) => x !== optionId)
          : [...current, optionId],
      };
    });
  };

  const ticked = Object.values(draft).reduce((n, ids) => n + (ids?.length ?? 0), 0);

  const commit = () => {
    setPart2(screenId, draft);
    clearDraft();
    onNext();
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        {copy?.title ?? "What you can already do"}
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy?.subtitle}</p>
      <div className="mt-4"><HowToAnswer>Tick what you can already do. Some questions take one answer, others take several — tap again to untick. Ticking nothing is fine.</HowToAnswer></div>
      <p className="mt-4 text-xs uppercase tracking-[0.16em] text-primary" aria-live="polite">
        {ticked === 0 ? "Nothing yet is completely fine" : `${ticked} ticked`}
      </p>

      <div className="mt-6 space-y-4">
        {items.map((item) => {
          const chosen = draft[item.item_id] ?? [];
          return (
            <fieldset
              key={item.item_id}
              className="rounded-3xl border border-border bg-card p-5"
            >
              <legend className="px-1 text-[15px] font-medium leading-snug">
                {item.prompt}
              </legend>
              {item.helper && (
                <p className="mt-1 text-xs text-muted-foreground">{item.helper}</p>
              )}
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {item.options.map((opt) => {
                  const on = chosen.includes(opt.option_id);
                  return (
                    <button
                      key={opt.option_id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(item.item_id, opt.option_id, item.multi)}
                      className={cn(
                        "min-h-[56px] rounded-2xl border px-4 py-3 text-left text-sm",
                        "flex items-start gap-3 transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        on
                          ? "border-primary bg-primary/10"
                          : "border-border bg-background hover:border-primary/40",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border",
                          on ? "border-primary bg-primary text-primary-foreground" : "border-border",
                        )}
                        aria-hidden="true"
                      >
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>

      <button
        type="button"
        onClick={commit}
        className="mt-8 min-h-[48px] w-full rounded-2xl bg-primary px-6 py-3
          font-medium text-primary-foreground transition-opacity hover:opacity-90
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
          focus-visible:ring-offset-2 focus-visible:ring-offset-background
          inline-flex items-center justify-center gap-2"
      >
        Continue
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
